'use client';

import React from 'react';
import ListCell from '@/components/ui/ListCell';
import { Divider } from '@/components/ui/Divider';
import { IconSearch, IconRewindClock } from '@repo/svgs';
import type { AiSearchSuggestion } from '@repo/api';
import type { RecentAiSearch } from '../utils/recentAiSearches';

interface AiSearchResultsProps {
  query: string;
  suggestions: AiSearchSuggestion[];
  recents: RecentAiSearch[];
  onSuggestion: (suggestion: AiSearchSuggestion) => void;
  onRecent: (recent: RecentAiSearch) => void;
}

function Rows({ children }: { children: React.ReactNode[] }) {
  return (
    <div className="flex flex-col gap-1 w-full">
      {children.map((child, idx) => (
        <React.Fragment key={idx}>
          {child}
          {idx !== children.length - 1 ?
            <div className="px-2">
              <Divider variant="tertiary" />
            </div> : null}
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * The list under the AI search bar. Shows recent searches when the input is
 * empty and the user has history; otherwise the backend-served starter prompts.
 * There's no per-keystroke typeahead (interpret is paid + on-submit only), so
 * while typing this keeps showing the starter prompts.
 */
export function AiSearchResults({
  query,
  suggestions,
  recents,
  onSuggestion,
  onRecent,
}: AiSearchResultsProps) {
  const isEmpty = query.trim().length === 0;
  const showRecents = isEmpty && recents.length > 0;

  if (showRecents) {
    return (
      <Rows>
        {recents.map(recent => (
          <ListCell
            key={recent.query}
            title={recent.query}
            leadingIcon={<IconRewindClock className="w-6 h-6 icon-secondary" />}
            trailing="chevron"
            onClick={() => onRecent(recent)}
            className="bg-transparent rounded-xl hover:bg-secondary"
          />
        ))}
      </Rows>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <Rows>
      {suggestions.map(suggestion => (
        <ListCell
          key={suggestion.url || suggestion.text}
          title={suggestion.text}
          leadingIcon={<IconSearch className="w-6 h-6 icon-secondary" />}
          trailing="chevron"
          onClick={() => onSuggestion(suggestion)}
          className="bg-transparent rounded-xl hover:bg-secondary"
        />
      ))}
    </Rows>
  );
}

export default AiSearchResults;
